import {
  BookOpenText,
  Circle,
  FolderMinus,
  FolderPlus,
  FolderX,
  Hourglass,
  type LucideIcon,
  Pencil,
  Search,
  SquarePen,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import type { AssistantActivityTurnItem } from "../../types";

const READ_TOOL_NAMES = new Set([
  "project_summary",
  "find_elements",
  "list_elements",
  "find_location",
  "element_get",
  "element_history",
  "element_history_diff",
]);

const KNOWLEDGE_TOOL_NAMES = new Set([
  // Retired by LAT-907 (document_search and document_list replaced it), kept so archived
  // transcripts still render their icon rather than falling back to the unknown-tool glyph.
  "find_attachments",
  "element_type_guidance",
  "knowledge_search",
  "quality_guard_issues_get",
  "search_bound_catalog",
  "skill_read",
]);

const DOCUMENT_TOOL_NAMES = new Set([
  "document_compare",
  "document_list",
  "document_outline",
  "document_read",
  "document_read_more",
  "document_read_original",
  "document_search",
]);

const ELEMENT_WRITE_TOOL_NAMES = new Set([
  "element_create",
  "element_rename",
  "element_attributes_update",
  "element_content_update",
  "element_move",
  "element_history_revert",
]);

function normalizeToolAction(action: string | null | undefined) {
  if (!action) {
    return null;
  }
  return action.split(".").pop() ?? action;
}

export function getAssistantActivityIconName(item: AssistantActivityTurnItem) {
  if (item.kind === "warning" || item.kind === "error") {
    return "triangle-alert";
  }
  if (item.kind === "user_input") {
    return "square-pen";
  }
  if (item.kind === "waiting") {
    return "hourglass";
  }
  if (
    item.kind !== "operation_summary" &&
    item.kind !== "runtime_status" &&
    item.kind !== "tool_activity"
  ) {
    return item.status === "in_progress" ? "circle" : "search";
  }

  const action = normalizeToolAction(item.toolActivity?.action);
  if (action && DOCUMENT_TOOL_NAMES.has(action)) {
    return "book-open-text";
  }
  if (action && READ_TOOL_NAMES.has(action)) {
    return "search";
  }
  if (action && KNOWLEDGE_TOOL_NAMES.has(action)) {
    return "book-open-text";
  }
  if (action && ELEMENT_WRITE_TOOL_NAMES.has(action)) {
    return "pencil";
  }
  if (action === "folder_create") {
    return "folder-plus";
  }
  if (action === "folder_rename") {
    return "folder-minus";
  }
  if (action === "folder_delete") {
    return "folder-x";
  }
  if (action === "element_delete") {
    return "trash-2";
  }
  return "square-pen";
}

export function getActivityIcon(
  iconName: ReturnType<typeof getAssistantActivityIconName>
): LucideIcon {
  switch (iconName) {
    case "book-open-text":
      return BookOpenText;
    case "circle":
      return Circle;
    case "folder-minus":
      return FolderMinus;
    case "folder-plus":
      return FolderPlus;
    case "folder-x":
      return FolderX;
    case "pencil":
      return Pencil;
    case "search":
      return Search;
    case "trash-2":
      return Trash2;
    case "triangle-alert":
      return TriangleAlert;
    case "square-pen":
      return SquarePen;
    case "hourglass":
      return Hourglass;
  }
}
